import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  createCatalog,
  getCatalog,
  getCatalogDocument,
  getCatalogEditHistory,
  getCatalogSettings,
  listCatalogTemplates,
  listOrderTypes,
  updateCatalog,
  updateCatalogDocument,
} from '../../app/api';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import { usePageTitle } from '../../layout/PageTitleContext';
import {
  CATALOG_STATUS_CODES,
  catalogStatusColors,
  catalogStatusI18nKey,
} from './catalogStatuses';
import CatalogEditHistoryTimeline from './private/CatalogEditHistoryTimeline';
import CatalogTemplateSelector from './private/CatalogTemplateSelector';
import CatalogInstanceEditor from './editor/CatalogInstanceEditor';
import {
  defaultInstanceDocument,
  serializeInstanceDocument,
} from './private/templateSpecUtils';
import { buildCatalogPublicLandingUrl } from './private/catalogPublicSlug';
import { parseCatalogDetail } from './private/catalogCompositionUtils';
import useUnsavedChangesGuard from '../../modules/core/public/useUnsavedChangesGuard';

const emptyCatalogForm = () => ({
  name: '',
  status: 'DRAFT',
  subtitle: '',
  hero_tags: [],
  promo_blocks: [],
  section_title: '',
  section_subtitle: '',
  layout_id: '',
  order_type_id: '',
});

const formSnapshot = (f) =>
  JSON.stringify({
    name: f.name ?? '',
    status: f.status || 'DRAFT',
    subtitle: f.subtitle ?? '',
    hero_tags: f.hero_tags ?? [],
    promo_blocks: f.promo_blocks ?? [],
    section_title: f.section_title ?? '',
    section_subtitle: f.section_subtitle ?? '',
    layout_id: f.layout_id ?? '',
    order_type_id: f.order_type_id ?? '',
  });

const AUTOSAVE_DEBOUNCE_MS = 800;

const catalogFromResponse = (catalog, payload) => ({
  name: catalog.name ?? payload.name ?? '',
  status: String(catalog.status || payload.status || 'DRAFT').toUpperCase(),
  subtitle: catalog.subtitle ?? payload.subtitle ?? '',
  hero_tags: Array.isArray(catalog.hero_tags) ? catalog.hero_tags : payload.hero_tags ?? [],
  promo_blocks: Array.isArray(catalog.promo_blocks)
    ? catalog.promo_blocks
    : payload.promo_blocks ?? [],
  section_title: catalog.section_title ?? payload.section_title ?? '',
  section_subtitle: catalog.section_subtitle ?? payload.section_subtitle ?? '',
  layout_id: catalog.layout_id ?? payload.layout_id ?? '',
  order_type_id: catalog.order_type_id ?? payload.order_type_id ?? '',
});

const CatalogFormPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { setPageTitle } = usePageTitle();
  const isNewRoute = location.pathname.endsWith('/new');
  const catalogId = isNewRoute ? null : id;

  const [form, setForm] = useState(emptyCatalogForm);
  const [loading, setLoading] = useState(true);
  const [saveNotice, setSaveNotice] = useState('');
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [templates, setTemplates] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [editorRefreshKey, setEditorRefreshKey] = useState(0);
  const [publicUrl, setPublicUrl] = useState('');
  const [baselineVersion, setBaselineVersion] = useState(0);
  const [editHistory, setEditHistory] = useState([]);
  const [documentJson, setDocumentJson] = useState(
    serializeInstanceDocument({ version: 2, blocks: {} }),
  );
  const [documentVersion, setDocumentVersion] = useState(0);
  const [catalogSettings, setCatalogSettings] = useState({});
  const [tab, setTab] = useState('content');
  const documentRef = useRef(documentJson);
  const createStartedRef = useRef(false);
  const hydratedRef = useRef(false);
  const baselineRef = useRef(null);
  const formRef = useRef(form);
  const catalogIdRef = useRef(catalogId);
  const loadSeqRef = useRef(0);
  const saveTimerRef = useRef(null);
  const saveNoticeTimerRef = useRef(null);
  const saveChainRef = useRef(Promise.resolve());
  const persistRef = useRef(null);
  const scheduleAutosaveRef = useRef(null);

  const isDeleted = form.status === 'DELETED';

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    catalogIdRef.current = catalogId;
  }, [catalogId]);

  useEffect(() => {
    documentRef.current = documentJson;
  }, [documentJson]);

  useEffect(() => {
    let cancelled = false;
    if (form.status !== 'PUBLISHED' || !catalogId || !currentWorkspace?.id) {
      setPublicUrl('');
      return undefined;
    }
    void buildCatalogPublicLandingUrl(currentWorkspace.id, catalogId).then((url) => {
      if (!cancelled) setPublicUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [form.status, catalogId, currentWorkspace?.id]);

  const setSaveNoticeState = useCallback((kind) => {
    clearTimeout(saveNoticeTimerRef.current);
    if (kind === 'saving') {
      setSaveNotice('saving');
      return;
    }
    if (kind === 'saved') {
      setSaveNotice('saved');
      saveNoticeTimerRef.current = setTimeout(() => setSaveNotice(''), 2000);
      return;
    }
    setSaveNotice('');
  }, []);

  useEffect(() => {
    const code = catalogId ? `#${catalogId}` : '';
    const label = isNewRoute && !catalogId ? translate('catalog.create') : translate('catalog.edit');
    setPageTitle(`${code ? `${code} – ` : ''}${label}`);
    return () => setPageTitle(null);
  }, [catalogId, isNewRoute, translate, setPageTitle]);

  const loadEditHistory = useCallback(async () => {
    if (!catalogId) return;
    try {
      const historyRes = await getCatalogEditHistory(catalogId);
      setEditHistory(Array.isArray(historyRes) ? historyRes : []);
    } catch {
      setEditHistory([]);
    }
  }, [catalogId]);

  const loadCatalog = useCallback(async () => {
    if (!catalogId) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    hydratedRef.current = false;
    baselineRef.current = null;
    try {
      const [catalogRes, docRes, settingsRes] = await Promise.all([
        getCatalog(catalogId),
        getCatalogDocument(catalogId),
        getCatalogSettings(),
        loadEditHistory(),
      ]);
      if (seq !== loadSeqRef.current) return;
      const { catalog } = parseCatalogDetail(catalogRes);
      const docPayload = docRes?.data?.document_json || docRes?.document_json;
      const nextDocument =
        docPayload || serializeInstanceDocument({ version: 2, blocks: {} });
      setDocumentJson(nextDocument);
      documentRef.current = nextDocument;
      const settingsPayload = settingsRes?.data ?? settingsRes;
      setCatalogSettings(settingsPayload || {});
      const loaded = {
        name: catalog.name ?? '',
        status: catalog.status || 'DRAFT',
        subtitle: catalog.subtitle ?? '',
        hero_tags: Array.isArray(catalog.hero_tags) ? catalog.hero_tags : [],
        promo_blocks: Array.isArray(catalog.promo_blocks) ? catalog.promo_blocks : [],
        section_title: catalog.section_title ?? '',
        section_subtitle: catalog.section_subtitle ?? '',
        layout_id: catalog.layout_id ?? '',
        order_type_id: catalog.order_type_id ?? '',
      };
      if (seq !== loadSeqRef.current) return;
      setForm(loaded);
      baselineRef.current = { ...loaded };
      setBaselineVersion((value) => value + 1);
      setEditorRefreshKey((value) => value + 1);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err.message);
    } finally {
      if (seq !== loadSeqRef.current) return;
      setLoading(false);
      hydratedRef.current = true;
    }
  }, [catalogId, loadEditHistory]);

  useEffect(() => {
    if (!isNewRoute || catalogId || createStartedRef.current) return;
    createStartedRef.current = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await createCatalog({});
        const created = res?.data || res;
        const createdCatalog = created?.catalog || created;
        if (!createdCatalog?.id) throw new Error(translate('catalog.create_failed'));
        navigate(`/catalogs/${createdCatalog.id}/edit`, { replace: true });
      } catch (err) {
        setError(err.message);
        createStartedRef.current = false;
        setLoading(false);
      }
    })();
  }, [isNewRoute, catalogId, navigate, translate]);

  useEffect(() => {
    if (catalogId) loadCatalog();
  }, [catalogId, loadCatalog]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listCatalogTemplates({ include_content: true });
        const list = res?.data ?? res;
        if (!cancelled) setTemplates(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listOrderTypes(true);
        const list = res?.data ?? res;
        if (!cancelled) setOrderTypes(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setOrderTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildPersistPayload = useCallback((source) => ({
    name: source.name,
    status: source.status,
    subtitle: source.subtitle,
    hero_tags: source.hero_tags,
    promo_blocks: source.promo_blocks,
    section_title: source.section_title,
    section_subtitle: source.section_subtitle,
    layout_id: source.layout_id,
    order_type_id: source.order_type_id,
  }), []);

  const scheduleAutosave = useCallback(
    (nextForm) => {
      if (!hydratedRef.current || isDeleted) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const base = baselineRef.current;
        if (!base || formSnapshot(nextForm) === formSnapshot(base)) return;
        void persistRef.current?.(buildPersistPayload(nextForm));
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [isDeleted, buildPersistPayload],
  );

  const reloadCatalogDocument = useCallback(async () => {
    if (!catalogId) return;
    const docRes = await getCatalogDocument(catalogId);
    const docPayload = docRes?.data?.document_json || docRes?.document_json;
    if (docPayload) {
      setDocumentJson(docPayload);
      documentRef.current = docPayload;
      setDocumentVersion((value) => value + 1);
    }
  }, [catalogId]);

  const persist = useCallback(
    (payload, options = {}) => {
      if (!catalogId) return Promise.resolve();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const savedSnapshot = formSnapshot(payload);
      const run = saveChainRef.current.then(async () => {
        setSaveNoticeState('saving');
        const res = await updateCatalog(catalogId, payload);
        const data = res?.data || res;
        const catalog = data?.catalog || data;
        if (catalog) {
          const saved = catalogFromResponse(catalog, payload);
          if (formSnapshot(formRef.current) === savedSnapshot) {
            baselineRef.current = { ...saved };
            setBaselineVersion((value) => value + 1);
            setForm((prev) => ({ ...prev, ...saved }));
            const serverStatus = saved.status;
            if (formRef.current.status !== serverStatus) {
              setForm((prev) => ({ ...prev, status: serverStatus }));
            }
          }
        }
        if (options.reloadDocument) {
          await reloadCatalogDocument();
        }
        setSaveNoticeState('saved');
        if (options.refreshPreview) {
          setEditorRefreshKey((value) => value + 1);
        }
        void loadEditHistory();
        const base = baselineRef.current;
        if (base && formSnapshot(formRef.current) !== formSnapshot(base)) {
          scheduleAutosaveRef.current?.(formRef.current);
        }
      });
      saveChainRef.current = run.catch((err) => {
        setSaveNoticeState('');
        setError(err.message);
      });
      return run;
    },
    [catalogId, setSaveNoticeState, loadEditHistory, reloadCatalogDocument],
  );

  useEffect(() => {
    persistRef.current = persist;
  }, [persist]);

  useEffect(() => {
    scheduleAutosaveRef.current = scheduleAutosave;
  }, [scheduleAutosave]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      clearTimeout(saveNoticeTimerRef.current);
      const base = baselineRef.current;
      const current = formRef.current;
      if (
        catalogIdRef.current &&
        hydratedRef.current &&
        base &&
        formSnapshot(current) !== formSnapshot(base) &&
        current.status !== 'DELETED'
      ) {
        void persistRef.current?.(buildPersistPayload(current));
      }
    },
    [buildPersistPayload],
  );

  const isMetadataDirty = useMemo(() => {
    if (!baselineRef.current || !hydratedRef.current) return false;
    return formSnapshot(form) !== formSnapshot(baselineRef.current);
  }, [form, baselineVersion]);

  const isPageDirty = isMetadataDirty;

  useUnsavedChangesGuard(isPageDirty && hydratedRef.current, translate('catalog.unsaved_leave_confirm'));

  const handleFormChange = useCallback(
    (update) => {
      setForm((prev) => {
        const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };
        scheduleAutosave(next);
        return next;
      });
    },
    [scheduleAutosave],
  );

  const handleStatusChange = (event) => {
    const nextStatus = event.target.value;
    const current = formRef.current;
    if (!catalogId || nextStatus === current.status) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setForm((prev) => ({ ...prev, status: nextStatus }));
    void persist(buildPersistPayload({ ...current, status: nextStatus }), { refreshPreview: true });
  };

  const handleTemplateChange = (nextLayoutId) => {
    if (!catalogId || isDeleted || nextLayoutId === formRef.current.layout_id) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const nextLayout = templates.find((item) => item.id === nextLayoutId);
    setForm((prev) => ({ ...prev, layout_id: nextLayoutId }));
    void persist(buildPersistPayload({ ...formRef.current, layout_id: nextLayoutId }), {
      refreshPreview: true,
      reloadDocument: false,
    }).then(() => {
      if (nextLayout?.document_json) {
        return resetInstanceForTemplate(nextLayout.document_json);
      }
      return undefined;
    });
  };

  const activeTemplate = useMemo(
    () => templates.find((item) => item.id === form.layout_id),
    [templates, form.layout_id],
  );

  const activeTemplateSpec = activeTemplate?.document_json || null;

  const resetInstanceForTemplate = useCallback(
    async (templateJson) => {
      if (!catalogId || !templateJson) return;
      const emptyDoc = serializeInstanceDocument(
        defaultInstanceDocument(templateJson, { usePlaceholders: false }),
      );
      setDocumentJson(emptyDoc);
      documentRef.current = emptyDoc;
      setDocumentVersion((value) => value + 1);
      await updateCatalogDocument(catalogId, { document_json: emptyDoc });
    },
    [catalogId],
  );

  const handleDocumentChange = useCallback(
    (serialized) => {
      if (!catalogId || isDeleted) return;
      documentRef.current = serialized;
      setDocumentJson(serialized);
      setEditorRefreshKey((value) => value + 1);
      setSaveNoticeState('saving');
      void updateCatalogDocument(catalogId, { document_json: serialized })
        .then(() => setSaveNoticeState('saved'))
        .catch((err) => setError(err.message));
    },
    [catalogId, isDeleted, setSaveNoticeState],
  );

  const handleOrderTypeChange = (event) => {
    handleFormChange({ order_type_id: event.target.value });
  };

  const handleNameChange = (event) => {
    handleFormChange({ name: event.target.value });
  };

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setSnackbar({ open: true, message: translate('catalog.public_url_copied') });
    } catch (_) {
      setError(translate('common.error'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{translate('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: 6 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        {catalogId && (
          <Chip label={`#${catalogId}`} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        )}
        <Chip
          size="small"
          label={translate(catalogStatusI18nKey(form.status))}
          color={catalogStatusColors[form.status] || 'default'}
        />
      </Box>

      {isDeleted && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('catalog.deleted_readonly')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {catalogId && (
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab value="content" label={translate('catalog.form_tab_content')} />
          <Tab value="alteracoes" label={translate('catalog.form_tab_alteracoes')} />
        </Tabs>
      )}

      {tab === 'alteracoes' && catalogId && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            {translate('catalog.edit_history_title')}
          </Typography>
          <CatalogEditHistoryTimeline events={editHistory} translate={translate} />
        </Paper>
      )}

      {tab === 'content' && (
      <Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              autoFocus={!isDeleted}
              disabled={isDeleted}
              label={translate('catalog.name')}
              placeholder={translate('catalog.name_placeholder')}
              value={form.name}
              onChange={handleNameChange}
            />
            <FormControl fullWidth disabled={isDeleted}>
              <InputLabel id="catalog-status-label">{translate('catalog.change_status')}</InputLabel>
              <Select
                labelId="catalog-status-label"
                label={translate('catalog.change_status')}
                value={form.status}
                onChange={handleStatusChange}
              >
                {CATALOG_STATUS_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {translate(catalogStatusI18nKey(code))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={isDeleted}>
              <InputLabel id="catalog-order-type-label">{translate('catalog.field_order_type')}</InputLabel>
              <Select
                labelId="catalog-order-type-label"
                label={translate('catalog.field_order_type')}
                value={form.order_type_id}
                onChange={handleOrderTypeChange}
              >
                <MenuItem value="">
                  <em>{translate('catalog.field_order_type_empty')}</em>
                </MenuItem>
                {orderTypes.map((ot) => (
                  <MenuItem key={ot.id} value={ot.id}>
                    {ot.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {publicUrl && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate('catalog.public_url')}
              </Typography>
              <TextField fullWidth size="small" value={publicUrl} InputProps={{ readOnly: true }} />
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('catalog.templates.field_template')}
          </Typography>
          <CatalogTemplateSelector
            templates={templates}
            value={form.layout_id}
            disabled={isDeleted}
            onChange={handleTemplateChange}
          />
        </Paper>

        {catalogId && !activeTemplateSpec && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {translate('catalog.templates.select_template_for_blocks')}
          </Alert>
        )}
        {catalogId && activeTemplateSpec && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <CatalogInstanceEditor
              key={`${form.layout_id}-${documentVersion}`}
              catalogId={catalogId}
              templateJson={activeTemplateSpec}
              instanceJson={documentJson}
              onInstanceChange={handleDocumentChange}
              readOnly={isDeleted}
              refreshKey={editorRefreshKey}
            />
          </Paper>
        )}
      </Box>
      )}

      {saveNotice && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.snackbar - 1,
            pointerEvents: 'none',
            px: 2,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {saveNotice === 'saving' ? translate('common.saving') : translate('common.saved')}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        sx={{ mb: saveNotice ? 5 : 0 }}
      />
    </Box>
  );
};

export default CatalogFormPage;
